/**
 * PostLike — unified view-layer adapter for posts.
 *
 * Purpose: UI components (UnifiedPostCell, UnifiedPostDialog) consume a single
 * normalized shape regardless of whether the underlying entity is a draft
 * `ContentPost` inside `post_generations/{runId}/generatedPosts/*`, a canonical
 * `content_posts/*` doc, or a sprint post under
 * `content_strategies/{sid}/sprints/{sprintId}/posts/*`.
 *
 * This is a VIEW-LAYER adapter — the Firestore schema is unchanged; adapters
 * convert between the canonical models and `PostLike` at surface boundaries.
 */

import type {
  ContentPost,
  PlatformId,
  AiAssetSlot,
  WhyForAudience,
  PostStatus,
  TopicDedupFingerprint,
} from '../types';

// BlockDataUsage is re-exported through the schema-models strategy bundle.
// It ships as `unknown`-compatible for the view layer — we only pass it
// through to DataMappingViewerV2 (which owns rendering).
export type BlockDataUsageLike = unknown;

export type PostLikeOwnerKind =
  | 'author_post_example'
  | 'content_post_draft'
  | 'content_post'
  | 'sprint_post';

/**
 * Normalized post shape consumed by all unified UI surfaces.
 *
 * - `ownerKind` + `ownerPath` together identify the Firestore doc for
 *   write-back. Hooks dispatched by the dialog use `ownerPath` directly so
 *   no per-kind branching is needed in the write layer.
 * - `platforms` is always an array. For single-platform post kinds the
 *   adapter wraps `post.platform` in `[platform]`; on save-back the adapter
 *   unwraps back to a single value.
 * - Audit fields (`whyForAudience`, `dataUsage`, `status`, etc.) are only
 *   present for post kinds; `PostExample` leaves them undefined.
 */
export interface PostLike {
  id: string;
  ownerKind: PostLikeOwnerKind;
  /** Firestore doc path for write-back. Never shown to the user. */
  ownerPath: string;
  projectId: string;

  // ─── Shared (editable) ─────────────────────────────────────────
  title?: string;
  platforms: PlatformId[];
  format?: string;
  hook?: string;
  body?: string;
  cta?: string;
  notes?: string;
  sourceUrl?: string;
  sourceCreatorHandle?: string;
  aiAssets?: Record<string, AiAssetSlot>;

  // ─── Optional metadata (post-kind specific) ───────────────────
  linkedTopicIds?: string[];
  addedBy?: string;

  // ─── Post-kind audit (read-only in UI) ─────────────────────────
  whyForAudience?: WhyForAudience[];
  dataUsage?: BlockDataUsageLike;
  dedupFingerprint?: TopicDedupFingerprint;
  status?: PostStatus;
  scheduledAt?: string;
  weekIndex?: number;
  weekdayIndex?: number;
  publishedAt?: string;
  failureReason?: string;

  // ─── Wave 2 — goal-driven generation surface (read-only) ───────
  /** Free-form post-level goal pre-assigned by SprintPlannerHandler. UI displays as 🎯 chip. */
  goal?: string | null;
  /** Reference to the PostTemplate that guided generation. UI displays as 📋 chip. */
  templateId?: string | null;
  /** direct | master | derivative. Discriminator for COPE chain vs single-step generation. */
  generationMode?: 'direct' | 'master' | 'derivative' | null;
  /** Master post id when this is a derivative — null/undefined for masters and direct posts. */
  sourcePostId?: string | null;
  /** A/B-substrate alternative hooks emitted alongside the primary. UI shows on hover/expand. */
  hookAlternatives?: string[];
  /**
   * v1.8.0 — Inline behavioural design brief produced by the design
   * sub-pipeline (AssetPreBriefing → Storyboarding → CardVisuals → Acceptance).
   * Absent on legacy posts; the UI shows an empty-state CTA in that case.
   */
  designAssetBrief?: unknown;
  generationPlanContext?: Record<string, unknown> | null;
  assetPlanningContext?: Record<string, unknown> | null;

  // ─── Post-kind context (used by promote-to-example, apply) ─────
  topicId?: string;
  strategyId?: string;
  sprintId?: string;
  runId?: string;
}

// ────────────────────────────────────────────────────────────────
// Path builders — three storage locations supported by the UI.
// ────────────────────────────────────────────────────────────────

export function contentPostDraftPath(projectId: string, runId: string, id: string): string {
  return `projects/${projectId}/post_generations/${runId}/generatedPosts/${id}`;
}

export function contentPostPath(projectId: string, id: string): string {
  return `projects/${projectId}/content_posts/${id}`;
}

export function sprintPostPath(
  projectId: string,
  strategyId: string,
  sprintId: string,
  id: string,
): string {
  return `projects/${projectId}/content_strategies/${strategyId}/sprints/${sprintId}/posts/${id}`;
}

// ────────────────────────────────────────────────────────────────
// Adapters: canonical → PostLike (read direction)
// ────────────────────────────────────────────────────────────────

export interface ContentPostAdapterCtx {
  kind: Extract<PostLikeOwnerKind, 'content_post_draft' | 'content_post' | 'sprint_post'>;
  projectId: string;
  /** Required for `content_post_draft` kind. */
  runId?: string;
  /** Required for `sprint_post` kind. */
  strategyId?: string;
  sprintId?: string;
}

export function contentPostToPostLike(
  post: ContentPost,
  ctx: ContentPostAdapterCtx,
): PostLike {
  // ownerPath is informational — any writeback hook that actually dispatches
  // against a doc checks its own required scope (runId / sprintId / strategyId)
  // before hitting Firestore. We intentionally don't throw on missing ctx
  // fields here because renderItem callbacks can fire during dialog close
  // animations where the run state has already been cleared; a crash would
  // abort the whole render tree. Callers that need a valid write path
  // validate on save instead.
  const ownerPath = (() => {
    switch (ctx.kind) {
      case 'content_post_draft':
        return ctx.runId
          ? contentPostDraftPath(ctx.projectId, ctx.runId, post.id)
          : `projects/${ctx.projectId}/post_generations/_unknown/generatedPosts/${post.id}`;
      case 'content_post':
        return contentPostPath(ctx.projectId, post.id);
      case 'sprint_post':
        return ctx.strategyId && ctx.sprintId
          ? sprintPostPath(ctx.projectId, ctx.strategyId, ctx.sprintId, post.id)
          : `projects/${ctx.projectId}/content_strategies/_unknown/sprints/_unknown/posts/${post.id}`;
    }
  })();
  return {
    id: post.id,
    ownerKind: ctx.kind,
    ownerPath,
    projectId: ctx.projectId,
    // ContentPost is single-platform; normalize to array for the view layer.
    platforms: post.platform ? [post.platform as PlatformId] : [],
    format: post.format as string | undefined,
    hook: post.hook,
    body: post.body,
    cta: post.cta,
    sourceUrl: (post as any).sourceUrl,
    sourceCreatorHandle: (post as any).sourceCreatorHandle,
    aiAssets: post.aiAssets as Record<string, AiAssetSlot> | undefined,
    whyForAudience: post.whyForAudience,
    dataUsage: post.dataUsage as BlockDataUsageLike | undefined,
    dedupFingerprint: post.dedupFingerprint,
    status: post.status,
    scheduledAt: post.scheduledAt ? new Date(post.scheduledAt).toISOString() : undefined,
    weekIndex: post.weekIndex,
    weekdayIndex: post.weekdayIndex,
    publishedAt: post.publishedAt ? new Date(post.publishedAt).toISOString() : undefined,
    failureReason: post.failureReason,
    topicId: post.topicId,
    strategyId: ctx.strategyId ?? post.strategyId,
    sprintId: ctx.sprintId ?? post.sprintId,
    runId: ctx.runId,
    // Wave 2 — goal-driven generation fields. ContentPost (schema 1.7.0) carries
    // these natively; PostLike surfaces them so chips render uniformly across
    // UnifiedPostCell / UnifiedPostDialog / sprint table without each consumer
    // needing to know about ContentPost vs Post (the local-Post type unifies
    // author_posts and content_posts and DOES NOT carry these fields — splitting
    // those collections is a separate AuthorPost-aggregate task documented in
    // `modules/content-plan/MODULE.md` outstanding-debts).
    goal: (post as any).goal ?? null,
    templateId: (post as any).templateId ?? null,
    generationMode: (post as any).generationMode ?? null,
    sourcePostId: (post as any).sourcePostId ?? null,
    hookAlternatives: (post as any).hookAlternatives,
    // v1.8.0 — surface the inline design brief so UnifiedPostDialog can
    // render DesignBriefDisplay above AiAssetsEditor.
    designAssetBrief: (post as any).designAssetBrief ?? undefined,
    generationPlanContext: (post as any).generationPlanContext ?? undefined,
    assetPlanningContext: (post as any).assetPlanningContext ?? undefined,
  };
}

// ────────────────────────────────────────────────────────────────
// Adapters: PostLike → canonical (write-back direction)
// ────────────────────────────────────────────────────────────────

/**
 * Build the patch that will be merged into an existing ContentPost doc.
 * Strips example-only fields (notes, sourceUrl, linkedTopicIds) and audit
 * fields (audit is read-only in the UI so we never write it back).
 */
export function postLikeToContentPostPatch(pl: PostLike): Partial<ContentPost> {
  return {
    // Single-platform — take the first element of the normalized array.
    platform: pl.platforms[0] as PlatformId | undefined,
    format: pl.format as ContentPost['format'],
    hook: pl.hook,
    body: pl.body,
    cta: pl.cta,
    aiAssets: pl.aiAssets,
    // Audit fields intentionally omitted — UI does not edit them.
  };
}
